import { GeckoFeatureModelType } from 'types/assetModel/assetModelEnums'
import {
  FeatureTableOptions,
  GeckoEditableFeature,
} from 'types/assetModel/features/baseClasses/geckoEditableFeature'
import { parseCoordinatesFromString } from 'types/assetModel/features/geckoPolygonalPlate'
import {
  ExternalUserFieldAccess,
  FormField,
  getFormFieldByDataKey,
  InputType,
  UnitMagnitude,
} from 'types/geckoFormTypesAndHelpers'
import { GeckoComponentSpace } from 'types/geometric/geckoComponentSpace'
import { GeckoMeasurement } from 'types/measurement/geckoMeasurement'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import {
  requiredCoordinatesArraySchema,
  requiredStringSchema,
} from 'utils/zodHelpers'

export interface GeckoShipDeckLadderJSON {
  type: GeckoFeatureModelType.ShipDeckLadder
  name: string
  feature_geometry: {
    coordinates: { x: number; y: number }[]
  }
}

export const NewGeckoShipDeckLadderJSON: GeckoShipDeckLadderJSON = {
  type: GeckoFeatureModelType.ShipDeckLadder,
  name: 'New Ship Deck Ladder',
  feature_geometry: {
    coordinates: [
      { x: 0, y: 0 }, // Start point
      { x: 0, y: 0 }, // First turn
      { x: 0, y: 0 }, // Second turn
      { x: 0, y: 0 }, // End point
    ],
  },
}

export class GeckoShipDeckLadder extends GeckoEditableFeature {
  readonly name: string
  readonly type = GeckoFeatureModelType.ShipDeckLadder
  readonly coordinates: {
    readonly x: GeckoMeasurement
    readonly y: GeckoMeasurement
  }[]

  /**
   * Private constructor, allowing for a factory method that can return undefined
   * @param id
   * @param name
   * @param coordinates
   */
  private constructor(
    id: string,
    name: string,
    coordinates: { x: GeckoMeasurement; y: GeckoMeasurement }[]
  ) {
    super(id)
    this.name = name
    this.coordinates = coordinates

    if (this.coordinates.length !== 4) {
      throw new Error('Ladder must have exactly 4 points in its geometry')
    }
  }

  // create from JSON
  public static CreateFromJSON(
    componentID: string,
    ladderID: string,
    ladderJSON: GeckoShipDeckLadderJSON
  ): GeckoShipDeckLadder {
    const polygonUnits = GeckoComponentSpace.CreateFromMeasurementUnits(
      componentID,
      GeckoMeasurementUnit.Meters,
      GeckoMeasurementUnit.Meters
    )

    const ladderCoordinates = ladderJSON.feature_geometry.coordinates.map(
      (coord) => ({
        x: GeckoMeasurement.CreateFromValue(coord.x, polygonUnits.x.unit),
        y: GeckoMeasurement.CreateFromValue(coord.y, polygonUnits.y.unit),
      })
    )

    const ladderName = ladderJSON.name as string

    return new GeckoShipDeckLadder(ladderID, ladderName, ladderCoordinates)
  }

  // create a map of all ladders
  public static CreateMapFromJSON(
    componentID: string,
    recordsJSON: Record<string, GeckoShipDeckLadderJSON>
  ): Map<string, GeckoShipDeckLadder> {
    return Object.keys(recordsJSON).reduce((resultObj, id) => {
      resultObj.set(
        id,
        GeckoShipDeckLadder.CreateFromJSON(componentID, id, recordsJSON[id])
      )
      return resultObj
    }, new Map<string, GeckoShipDeckLadder>())
  }

  asJSON() {
    return generateLadder(this.name, this.coordinates)
  }
}

export function generateLadder(
  name: string,
  coordinates: { x: GeckoMeasurement; y: GeckoMeasurement }[]
): GeckoShipDeckLadderJSON {
  return {
    name: name,
    type: GeckoFeatureModelType.ShipDeckLadder,
    feature_geometry: {
      coordinates: coordinates.map((coord) => ({
        x: coord.x.toNumber(GeckoMeasurementUnit.Meters),
        y: coord.y.toNumber(GeckoMeasurementUnit.Meters),
      })),
    },
  }
}

export const ShipDeckLadderFormFields = (
  ladderJSON: GeckoShipDeckLadderJSON
) => {
  return [
    {
      dataKey: 'name',
      label: 'Ladder Name',
      value: ladderJSON.name,
      unit_magnitude: null,
      validationRule: requiredStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Text,
    },
    {
      dataKey: 'coordinates',
      label: 'Coordinates',
      value: ladderJSON.feature_geometry.coordinates
        .map((coord) => `[${coord.x}, ${coord.y}]`)
        .join(', '),
      unit_magnitude: UnitMagnitude.FeetOrMeters,
      validationRule: requiredCoordinatesArraySchema(4),
      externalUserAccess: ExternalUserFieldAccess.ReadOnly,
      isNeededForDerivedLayer: false,
      inputType: InputType.Coordinates,
    },
  ]
}

export const ShipDeckLadderJSONFromFormFields = (formFields: FormField[]) => {
  const coordinates = getFormFieldByDataKey(formFields, 'coordinates')
    ?.value as string

  return generateLadder(
    getFormFieldByDataKey(formFields, 'name')?.value as string,
    parseCoordinatesFromString(coordinates).map(({ x, y }) => ({
      x: GeckoMeasurement.CreateFromValue(x, GeckoMeasurementUnit.Meters),
      y: GeckoMeasurement.CreateFromValue(y, GeckoMeasurementUnit.Meters),
    }))
  )
}

export const ShipDeckLadderFeatureTableOptions: FeatureTableOptions = {
  type: GeckoFeatureModelType.ShipDeckLadder,
  title: 'Ladders',
  singularName: 'Ladder',
  fields: ['name', 'coordinates'],
  tableMessage:
    'To see how to enter coordinates to render a drain correctly, please see the <a href="https://geckorobotics.atlassian.net/wiki/spaces/SE/pages/4595744769/Polygonal+Plates" target="_blank">documentation</a> on polygonal plates.',
}
